// CROSSFIRE — Play menu hub.
// A finite-state flow over three top-level categories:
//   COMBAT (visual only; matchmaking planned)
//   PRACTICE (fully playable vs bots — Round / Rapid / Solo Drill)
//   SERVERS (browse + create — planned)
//
// Every unavailable action shows a polished BetaUnavailable dialog so the
// player is never left staring at a dead button.
import { useEffect, useMemo, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useSettings } from "@/stores/settingsStore";
import { PLAY } from "@/constants/testIds";
import { useUiSounds } from "@/lib/audio";
import LobbyLayout from "@/components/LobbyLayout";
import {
    MAPS,
    TEAM_SIZE_CATALOG,
    WEATHER,
    TIME_OF_DAY,
    ROUND_COUNTS,
    TIME_LIMITS,
    SCORE_LIMITS,
    mapsForTeamSize,
} from "@/game/systems/maps";
import { ENEMY_DIFFICULTY, FRIENDLY_DIFFICULTY, DIFFICULTY_KEYS } from "@/game/systems/BotAI";
import {
    ChevronLeft,
    ChevronRight,
    Play,
    Lock,
    Swords,
    Crosshair,
    Server,
    Users,
    Trophy,
    Zap,
    Timer,
    Target,
    Bookmark,
    RefreshCw,
    Copy,
    Search,
    ShieldOff,
    Info,
    AlertTriangle,
    Sparkles,
    Bot,
    Save,
} from "lucide-react";

// Re-export MAPS for legacy imports (some screens read from here)
export { MAPS } from "@/game/systems/maps";

const CATEGORIES = {
    HUB: "hub",
    COMBAT: "combat",
    PRACTICE: "practice",
    SERVERS: "servers",
};

// -------------------- Root ----------------------------------------------------

export default function PlayMenu() {
    const goto = useGameStore((s) => s.goto);
    const sfx = useUiSounds();
    const [screen, setScreen] = useState(CATEGORIES.HUB);
    const [betaDialog, setBetaDialog] = useState(null);

    const backToLobby = () => {
        sfx.close();
        goto(SCREEN.MENU);
    };

    return (
        <LobbyLayout active={SCREEN.PLAY}>
            <div
                data-testid={PLAY.root}
                className="relative w-full h-full text-white overflow-hidden"
                style={{
                    background:
                        "radial-gradient(1200px 500px at 50% -10%, rgba(255,154,74,0.10), transparent 60%), radial-gradient(900px 500px at 50% 110%, rgba(0,0,0,0.7), transparent 60%)",
                }}
            >
                <FlowHeader
                    screen={screen}
                    onHub={() => { sfx.close(); setScreen(CATEGORIES.HUB); }}
                    onBack={backToLobby}
                />

                <div className="absolute inset-x-0 top-16 bottom-0 overflow-y-auto">
                    {screen === CATEGORIES.HUB && (
                        <CategoryHub
                            onSelect={(id) => { sfx.click(); setScreen(id); }}
                        />
                    )}
                    {screen === CATEGORIES.COMBAT && (
                        <CombatFlow
                            onUnavailable={(dlg) => setBetaDialog(dlg)}
                            onGoPractice={() => { sfx.click(); setScreen(CATEGORIES.PRACTICE); }}
                        />
                    )}
                    {screen === CATEGORIES.PRACTICE && (
                        <PracticeFlow />
                    )}
                    {screen === CATEGORIES.SERVERS && (
                        <ServersFlow
                            onUnavailable={(dlg) => setBetaDialog(dlg)}
                        />
                    )}
                </div>

                <BetaUnavailable
                    dialog={betaDialog}
                    onClose={() => setBetaDialog(null)}
                    onGoPractice={() => {
                        setBetaDialog(null);
                        setScreen(CATEGORIES.PRACTICE);
                    }}
                />
            </div>
        </LobbyLayout>
    );
}

// -------------------- Header --------------------------------------------------

function FlowHeader({ screen, onHub, onBack }) {
    const label =
        screen === CATEGORIES.HUB
            ? { crumb: "SELECT CATEGORY", title: "PLAY" }
            : screen === CATEGORIES.COMBAT
              ? { crumb: "COMBAT · ONLINE (BETA LOCKED)", title: "COMBAT" }
              : screen === CATEGORIES.PRACTICE
                ? { crumb: "PRACTICE · OFFLINE VS AI", title: "PRACTICE" }
                : { crumb: "SERVERS · CUSTOM (COMING SOON)", title: "SERVERS" };

    return (
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-8 py-3 border-b border-white/5 bg-black/30 backdrop-blur">
            <div className="flex items-center gap-3">
                <button
                    data-testid={PLAY.back}
                    onClick={screen === CATEGORIES.HUB ? onBack : onHub}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                >
                    <ChevronLeft size={16} /> {screen === CATEGORIES.HUB ? "Lobby" : "Categories"}
                </button>
                {screen !== CATEGORIES.HUB && (
                    <button
                        onClick={onBack}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                        title="Return to Lobby"
                    >
                        Lobby
                    </button>
                )}
            </div>
            <div className="flex flex-col items-center">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                    {label.crumb}
                </div>
                <div className="axis-heading text-2xl tracking-[0.4em] text-white">{label.title}</div>
            </div>
            <div className="w-[220px] flex justify-end axis-mono text-[10px] tracking-[0.4em] text-white/40">
                CROSSFIRE · AXIS
            </div>
        </div>
    );
}

// -------------------- Category Hub -------------------------------------------

function CategoryHub({ onSelect }) {
    const cards = [
        {
            id: CATEGORIES.COMBAT,
            testId: PLAY.categoryCombat,
            title: "COMBAT",
            subtitle: "COMPETITIVE · ONLINE",
            description:
                "Ranked ladder and open online matches with real players. Everything is browsable — matchmaking activates in a future update.",
            icon: Swords,
            accent: "#ff9a4a",
            status: "BETA · LOCKED",
            statusColor: "#ffb300",
            bg: "linear-gradient(135deg, rgba(120,60,20,0.35) 0%, rgba(20,20,20,0.9) 60%, rgba(10,10,10,1) 100%)",
        },
        {
            id: CATEGORIES.PRACTICE,
            testId: PLAY.categoryPractice,
            title: "PRACTICE",
            subtitle: "OFFLINE · VS AI",
            description:
                "The main playable Beta mode. Round Combat, Rapid Assault and Solo Drill. Fine-grained bot and rule controls.",
            icon: Crosshair,
            accent: "#00e5ff",
            status: "PLAYABLE",
            statusColor: "#00ff88",
            bg: "linear-gradient(135deg, rgba(20,80,90,0.35) 0%, rgba(20,20,20,0.9) 60%, rgba(10,10,10,1) 100%)",
        },
        {
            id: CATEGORIES.SERVERS,
            testId: PLAY.categoryServers,
            title: "SERVERS",
            subtitle: "CUSTOM · PRIVATE / PUBLIC",
            description:
                "Browse and host custom sessions. Hosting and joining go live once online multiplayer is available.",
            icon: Server,
            accent: "#c084fc",
            status: "COMING SOON",
            statusColor: "#c084fc",
            bg: "linear-gradient(135deg, rgba(60,40,90,0.35) 0%, rgba(20,20,20,0.9) 60%, rgba(10,10,10,1) 100%)",
        },
    ];

    return (
        <div
            data-testid={PLAY.hub}
            className="max-w-[1400px] mx-auto px-8 py-10"
        >
            <div className="axis-mono text-[11px] tracking-[0.5em] text-white/50 mb-3 axis-fade-in">
                CHOOSE HOW YOU DEPLOY
            </div>
            <div className="axis-heading text-4xl tracking-[0.3em] text-white mb-8 axis-slide-up">
                MISSION SELECT
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {cards.map((c, idx) => {
                    const Icon = c.icon;
                    return (
                        <button
                            key={c.id}
                            data-testid={c.testId}
                            onClick={() => onSelect(c.id)}
                            className="group relative text-left axis-cut border border-white/10 hover:border-white/30 transition overflow-hidden axis-slide-up"
                            style={{
                                background: c.bg,
                                animationDelay: `${idx * 100}ms`,
                                minHeight: 340,
                            }}
                        >
                            {/* Subtle animated dust overlay */}
                            <div className="pointer-events-none absolute inset-0 opacity-40 mix-blend-screen group-hover:opacity-60 transition-opacity"
                                 style={{
                                     background:
                                         "radial-gradient(600px 200px at 20% 20%, rgba(255,255,255,0.04), transparent 60%), radial-gradient(400px 200px at 80% 80%, rgba(255,255,255,0.03), transparent 60%)",
                                 }}
                            />
                            {/* Icon watermark */}
                            <div className="absolute right-4 top-4 opacity-15 group-hover:opacity-30 transition-opacity">
                                <Icon size={140} style={{ color: c.accent }} strokeWidth={1} />
                            </div>

                            <div className="relative z-10 p-7 flex flex-col h-full">
                                <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50">
                                    {c.subtitle}
                                </div>
                                <div
                                    className="axis-heading text-5xl tracking-[0.2em] mt-1"
                                    style={{ color: c.accent }}
                                >
                                    {c.title}
                                </div>
                                <div className="axis-mono text-sm text-white/70 mt-4 leading-relaxed max-w-md">
                                    {c.description}
                                </div>

                                <div className="mt-auto pt-6 flex items-center justify-between">
                                    <span
                                        className="axis-mono text-[10px] tracking-[0.4em] px-2 py-1 border axis-cut-sm"
                                        style={{
                                            borderColor: c.statusColor,
                                            color: c.statusColor,
                                            background: `${c.statusColor}12`,
                                        }}
                                    >
                                        {c.status}
                                    </span>
                                    <span className="flex items-center gap-2 axis-mono text-[10px] tracking-[0.4em] text-white/80 group-hover:text-white transition-colors">
                                        ENTER <ChevronRight size={14} />
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="mt-8 flex items-start gap-3 text-white/50 axis-mono text-[11px] leading-relaxed max-w-3xl">
                <Info size={14} className="mt-0.5 text-orange-300/70" />
                <div>
                    <span className="text-white/80">Beta status</span> — Practice is fully playable. Combat and Servers show the full future interface so
                    you can inspect maps, modes and rules before online multiplayer ships.
                </div>
            </div>
        </div>
    );
}

// -------------------- Beta Unavailable Dialog --------------------------------

function BetaUnavailable({ dialog, onClose, onGoPractice }) {
    useEffect(() => {
        if (!dialog) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [dialog, onClose]);

    if (!dialog) return null;
    return (
        <div
            data-testid={PLAY.betaUnavailable}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md axis-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="axis-glass axis-cut max-w-lg w-[92%] p-8 border-orange-400/40"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 border border-orange-400/60 axis-cut-sm">
                        <AlertTriangle size={20} className="text-orange-300" />
                    </div>
                    <div>
                        <div className="axis-mono text-[10px] tracking-[0.4em] text-orange-300/80">
                            {dialog.crumb || "BETA · UNAVAILABLE"}
                        </div>
                        <div className="axis-heading text-xl text-white tracking-widest">
                            {dialog.title}
                        </div>
                    </div>
                </div>
                <div className="axis-mono text-sm text-white/70 leading-relaxed whitespace-pre-line">
                    {dialog.body}
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    {dialog.showPractice !== false && (
                        <button
                            data-testid={PLAY.betaUnavailableGoPractice}
                            onClick={onGoPractice}
                            className="axis-btn axis-cut-sm px-5 py-2 text-xs border border-cyan-400 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/30 flex items-center gap-2"
                        >
                            <Crosshair size={13} /> GO TO PRACTICE
                        </button>
                    )}
                    <button
                        data-testid={PLAY.betaUnavailableClose}
                        onClick={onClose}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-5 py-2 text-xs"
                    >
                        CLOSE
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------------- Combat Flow --------------------------------------------

function CombatFlow({ onUnavailable, onGoPractice }) {
    const sfx = useUiSounds();
    const [mode, setMode] = useState(null); // 'ranked' | 'open'

    if (!mode) {
        return (
            <div className="max-w-6xl mx-auto px-8 py-10">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50 mb-4">
                    COMBAT · PICK YOUR LADDER
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SubCategoryCard
                        testId={PLAY.combatRanked}
                        icon={Trophy}
                        accent="#ffb300"
                        title="RANKED COMBAT"
                        subtitle="COMPETITIVE · SEASONAL"
                        description="Small competitive teams. Wins and performance move your rating up or down. Placement, seasons and rank history planned for the multiplayer launch."
                        badge="LADDER LOCKED"
                        onClick={() => { sfx.click(); setMode("ranked"); }}
                    />
                    <SubCategoryCard
                        testId={PLAY.combatOpen}
                        icon={Swords}
                        accent="#ff9a4a"
                        title="OPEN COMBAT"
                        subtitle="NON-RANKED · XP ONLY"
                        description="Standard online matches with no rating loss. Larger team sizes and looser rules — the sandbox for testing loadouts."
                        badge="XP ONLY"
                        onClick={() => { sfx.click(); setMode("open"); }}
                    />
                </div>
            </div>
        );
    }

    return (
        <CombatModeBuilder
            variant={mode}
            onBack={() => { sfx.close(); setMode(null); }}
            onUnavailable={onUnavailable}
            onGoPractice={onGoPractice}
        />
    );
}

function SubCategoryCard({ testId, icon: Icon, accent, title, subtitle, description, badge, onClick }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className="group text-left axis-cut border border-white/10 hover:border-white/30 transition p-7 relative overflow-hidden"
            style={{ minHeight: 240, background: "linear-gradient(135deg, rgba(30,30,30,0.7), rgba(10,10,10,0.9))" }}
        >
            <Icon
                size={120}
                className="absolute -right-4 -top-4 opacity-15 group-hover:opacity-30 transition-opacity"
                style={{ color: accent }}
                strokeWidth={1}
            />
            <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50">{subtitle}</div>
            <div className="axis-heading text-3xl tracking-[0.2em] mt-1" style={{ color: accent }}>
                {title}
            </div>
            <div className="axis-mono text-sm text-white/70 mt-3 leading-relaxed max-w-md">
                {description}
            </div>
            <div className="mt-4 flex items-center justify-between">
                <span
                    className="axis-mono text-[10px] tracking-[0.4em] px-2 py-1 border axis-cut-sm"
                    style={{ borderColor: accent, color: accent, background: `${accent}12` }}
                >
                    {badge}
                </span>
                <span className="flex items-center gap-2 axis-mono text-[10px] tracking-[0.4em] text-white/70 group-hover:text-white">
                    OPEN <ChevronRight size={14} />
                </span>
            </div>
        </button>
    );
}

function CombatModeBuilder({ variant, onBack, onUnavailable }) {
    const sfx = useUiSounds();
    const isRanked = variant === "ranked";
    const teamSizes = TEAM_SIZE_CATALOG.filter((t) =>
        isRanked ? ["1v1", "2v2", "3v3", "5v5"].includes(t.id) : true
    );
    const [teamSize, setTeamSize] = useState(isRanked ? "5v5" : "5v5");
    const [rule, setRule] = useState("round"); // ignored for ranked (round only)
    const [mapId, setMapId] = useState(() => (mapsForTeamSize(teamSize)[0]?.id ?? "training_yard"));

    useEffect(() => {
        const filtered = mapsForTeamSize(teamSize);
        if (!filtered.find((m) => m.id === mapId)) setMapId(filtered[0]?.id);
    }, [teamSize]); // eslint-disable-line

    const attemptStart = () => {
        sfx.click();
        onUnavailable({
            crumb: isRanked ? "COMBAT · RANKED" : "COMBAT · OPEN",
            title: "Online Combat is not available in the Beta version",
            body:
                "Online matchmaking and ranked progression will be introduced in a future update.\n\nYou can currently play Practice matches against bots — every mode, team size and map option is fully configurable there.",
            showPractice: true,
        });
    };

    return (
        <div className="max-w-6xl mx-auto px-8 py-8">
            <FlowSubheader
                onBack={onBack}
                crumb={isRanked ? "COMBAT · RANKED" : "COMBAT · OPEN"}
                title={isRanked ? "RANKED COMBAT" : "OPEN COMBAT"}
            />

            {isRanked && <RankedPanel />}

            {!isRanked && (
                <>
                    <SectionLabel>MATCH RULE</SectionLabel>
                    <RulePicker value={rule} onChange={setRule} />
                </>
            )}

            <SectionLabel>TEAM SIZE</SectionLabel>
            <TeamSizePicker value={teamSize} onChange={setTeamSize} sizes={teamSizes} />

            <SectionLabel>MAP</SectionLabel>
            <MapPicker teamSize={teamSize} value={mapId} onChange={setMapId} allowUnplayable />

            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    onClick={onBack}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-6 py-3 text-xs flex items-center gap-2"
                >
                    <ChevronLeft size={14} /> Back
                </button>
                <button
                    data-testid={PLAY.deploy}
                    onClick={attemptStart}
                    className="axis-btn axis-cut-sm px-8 py-3 text-sm border border-orange-400 bg-orange-500/15 text-orange-100 flex items-center gap-2 hover:bg-orange-500/30"
                >
                    <Play size={14} /> QUEUE FOR MATCH
                    <Lock size={12} className="ml-1 text-orange-300" />
                </button>
            </div>
        </div>
    );
}

function RankedPanel() {
    return (
        <div className="axis-cut border border-white/10 p-6 mb-8"
             style={{ background: "linear-gradient(135deg, rgba(50,35,10,0.5), rgba(10,10,10,0.9))" }}>
            <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                CROSSFIRE LADDER · BETA SEASON
            </div>
            <div className="axis-heading text-3xl tracking-[0.3em] text-white mt-1">
                UNRANKED
            </div>
            <div className="axis-mono text-sm text-white/60 mt-2">
                Placement matches will unlock when online multiplayer ships.
            </div>

            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                <RankStat
                    label="Placement Matches"
                    value="0 / 5"
                    hint="Complete 5 games to place"
                    highlight
                />
                <RankStat label="Ranked Victories" value="0" />
                <RankStat label="Season" value="Beta" />
                <RankStat label="Rating" value="—" hint="Not available" />
            </div>
            <div className="mt-4 axis-mono text-[10px] tracking-[0.4em] text-white/40">
                RANK HISTORY · COMING SOON
            </div>
        </div>
    );
}
function RankStat({ label, value, hint, highlight }) {
    return (
        <div
            className={`axis-cut-sm border p-3 ${
                highlight ? "border-orange-400/40 bg-orange-500/[0.04]" : "border-white/10 bg-white/[0.02]"
            }`}
        >
            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40">{label}</div>
            <div
                className={`axis-heading text-lg mt-1 ${highlight ? "text-orange-200" : "text-white"}`}
            >
                {value}
            </div>
            {hint && (
                <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40 mt-1">
                    {hint.toUpperCase()}
                </div>
            )}
        </div>
    );
}

// -------------------- Practice Flow ------------------------------------------

const PRACTICE_PRESET_KEY = "crossfire.practice.preset";

function PracticeFlow() {
    const sfx = useUiSounds();
    const goto = useGameStore((s) => s.goto);
    const setMatchConfig = useGameStore((s) => s.setMatchConfig);
    const updateSettings = useSettings((s) => s.update);

    // Load last preset (or defaults)
    const initial = useMemo(() => {
        try {
            const raw = typeof window !== "undefined"
                ? window.localStorage.getItem(PRACTICE_PRESET_KEY)
                : null;
            if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        return null;
    }, []);

    const [rule, setRule] = useState(initial?.rule || "round");
    const [teamSize, setTeamSize] = useState(initial?.teamSize || "1v1");
    const initialMap = mapsForTeamSize(teamSize).find((m) => m.playable);
    const [mapId, setMapId] = useState(initial?.mapId || initialMap?.id || "training_yard");
    const [weather, setWeather] = useState(initial?.weather || "clear");
    const [timeOfDay, setTimeOfDay] = useState(initial?.timeOfDay || "day");
    const [rounds, setRounds] = useState(initial?.rounds || 3);
    const [timeLimit, setTimeLimit] = useState(initial?.timeLimit || 600);
    const [scoreLimit, setScoreLimit] = useState(initial?.scoreLimit ?? 50);
    const [enemyDiff, setEnemyDiff] = useState(initial?.enemyDiff || "easy");
    const [friendlyDiff, setFriendlyDiff] = useState(initial?.friendlyDiff || "normal");
    const [friendlyFire, setFriendlyFire] = useState(!!initial?.friendlyFire);
    const [autoBalance, setAutoBalance] = useState(initial?.autoBalance !== false);
    const [step, setStep] = useState("config"); // 'config' | 'review'

    // If team size changes, reset map to first playable one for that size
    useEffect(() => {
        const list = mapsForTeamSize(teamSize);
        if (!list.find((m) => m.id === mapId)) {
            const firstPlayable = list.find((m) => m.playable) || list[0];
            if (firstPlayable) setMapId(firstPlayable.id);
        }
    }, [teamSize]); // eslint-disable-line

    const selectedMap = MAPS.find((m) => m.id === mapId);
    const perfHint = (() => {
        const size = TEAM_SIZE_CATALOG.find((t) => t.id === teamSize);
        if (!size) return { label: "STANDARD", color: "#00ff88" };
        if (size.perfCost === "low") return { label: "LOW COST", color: "#00ff88" };
        if (size.perfCost === "med") return { label: "STANDARD", color: "#00e5ff" };
        if (size.perfCost === "high") return { label: "HIGH COST", color: "#ffb300" };
        return { label: "EXPERIMENTAL", color: "#ff5060" };
    })();

    const savePreset = () => {
        const payload = {
            rule, teamSize, mapId, weather, timeOfDay,
            rounds, timeLimit, scoreLimit,
            enemyDiff, friendlyDiff, friendlyFire, autoBalance,
        };
        try { window.localStorage.setItem(PRACTICE_PRESET_KEY, JSON.stringify(payload)); } catch {/*noop*/}
        sfx.click();
    };

    const startMatch = () => {
        savePreset();
        // Persist last-picked loadout on the profile (legacy convenience)
        updateSettings({ lastLoadout: { mapId, teamSize, difficulty: enemyDiff } });
        setMatchConfig({
            mapId,
            teamSize,
            rule,
            roundsToWin: rounds,
            timeLimitSeconds: timeLimit,
            scoreLimit,
            weather,
            timeOfDay,
            // Legacy field, still consumed by matchStore + FPSGame
            difficulty: enemyDiff,
            enemyDifficulty: enemyDiff,
            friendlyDifficulty: friendlyDiff,
            friendlyFire,
            autoBalance,
        });
        goto(SCREEN.MATCH);
    };

    if (step === "review") {
        return (
            <ReviewScreen
                cfg={{
                    category: "PRACTICE",
                    mode: RULE_LABELS[rule],
                    teamSize,
                    mapId,
                    mapName: selectedMap?.name || mapId,
                    weather,
                    timeOfDay,
                    rounds,
                    timeLimit,
                    scoreLimit,
                    enemyDiff,
                    friendlyDiff,
                    friendlyFire,
                    perfHint,
                    rule,
                }}
                onBack={() => { sfx.close(); setStep("config"); }}
                onDeploy={startMatch}
                onSavePreset={savePreset}
            />
        );
    }

    // Solo Drill takes over the whole flow with its own settings
    if (rule === "solo_drill") {
        return (
            <SoloDrillFlow
                onBack={() => { sfx.close(); setStep("config"); setRule("round"); }}
                onDeploy={(overrides) => {
                    updateSettings({ lastLoadout: { mapId, teamSize: "1v1", difficulty: "easy" } });
                    setMatchConfig({
                        mapId,
                        teamSize: "1v1",
                        rule: "solo_drill",
                        roundsToWin: 99,
                        timeLimitSeconds: 0,
                        scoreLimit: 0,
                        weather,
                        timeOfDay,
                        difficulty: "easy",
                        enemyDifficulty: "easy",
                        friendlyDifficulty: "easy",
                        friendlyFire: false,
                        friendlyCount: 0,
                        enemyCount: overrides?.enemyCount ?? 3,
                        soloDrill: true,
                    });
                    goto(SCREEN.MATCH);
                }}
                onPickMap={(id) => setMapId(id)}
                mapId={mapId}
            />
        );
    }

    return (
        <div className="max-w-[1180px] mx-auto px-8 py-6">
            <StepBar current={1} steps={["Configure", "Review", "Deploy"]} />

            <SectionLabel>MATCH RULE</SectionLabel>
            <RulePicker value={rule} onChange={setRule} includeSoloDrill />

            <SectionLabel>TEAM SIZE</SectionLabel>
            <TeamSizePicker value={teamSize} onChange={setTeamSize} sizes={TEAM_SIZE_CATALOG} />

            <SectionLabel>MAP · {perfHint.label}</SectionLabel>
            <MapPicker teamSize={teamSize} value={mapId} onChange={setMapId} allowUnplayable={false} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                    <SectionLabel>WEATHER</SectionLabel>
                    <ChipPicker
                        options={WEATHER}
                        value={weather}
                        onChange={setWeather}
                        testIdPrefix={PLAY.weatherPrefix}
                    />
                </div>
                <div>
                    <SectionLabel>TIME OF DAY</SectionLabel>
                    <ChipPicker
                        options={TIME_OF_DAY}
                        value={timeOfDay}
                        onChange={setTimeOfDay}
                        testIdPrefix={PLAY.todPrefix}
                    />
                </div>
            </div>

            {rule === "round" ? (
                <>
                    <SectionLabel>ROUNDS TO WIN</SectionLabel>
                    <ChipPicker
                        options={ROUND_COUNTS}
                        value={rounds}
                        onChange={setRounds}
                        testIdPrefix="rounds-"
                    />
                </>
            ) : (
                <>
                    <SectionLabel>TIME LIMIT</SectionLabel>
                    <ChipPicker
                        options={TIME_LIMITS}
                        value={timeLimit}
                        onChange={setTimeLimit}
                        testIdPrefix="time-"
                    />
                    <SectionLabel>SCORE LIMIT</SectionLabel>
                    <ChipPicker
                        options={SCORE_LIMITS}
                        value={scoreLimit}
                        onChange={setScoreLimit}
                        testIdPrefix="score-"
                    />
                </>
            )}

            <SectionLabel>ENEMY BOTS</SectionLabel>
            <DifficultyPicker
                value={enemyDiff}
                onChange={setEnemyDiff}
                table={ENEMY_DIFFICULTY}
                testIdPrefix={PLAY.enemyDiffPrefix}
            />

            <SectionLabel>FRIENDLY BOTS</SectionLabel>
            <DifficultyPicker
                value={friendlyDiff}
                onChange={setFriendlyDiff}
                table={FRIENDLY_DIFFICULTY}
                testIdPrefix={PLAY.friendlyDiffPrefix}
                accent="#00e5ff"
            />

            <SectionLabel>OPTIONS</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <ToggleRow
                    label="Friendly Fire"
                    sub="Allies can damage the player"
                    value={friendlyFire}
                    onChange={setFriendlyFire}
                    testId="toggle-friendly-fire"
                />
                <ToggleRow
                    label="Auto-Balance"
                    sub="Fill slots to keep teams even"
                    value={autoBalance}
                    onChange={setAutoBalance}
                    testId="toggle-auto-balance"
                />
                <div className="axis-cut-sm border border-white/10 bg-white/[0.02] p-3">
                    <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40">Performance</div>
                    <div className="axis-heading text-sm mt-1" style={{ color: perfHint.color }}>
                        {perfHint.label}
                    </div>
                </div>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    data-testid={PLAY.savePreset}
                    onClick={savePreset}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-5 py-3 text-xs flex items-center gap-2"
                >
                    <Save size={13} /> SAVE PRESET
                </button>
                <button
                    data-testid="play-review-btn"
                    onClick={() => { sfx.click(); setStep("review"); }}
                    className="axis-btn axis-cut-sm px-8 py-3 text-sm border border-cyan-400 bg-cyan-500/15 text-cyan-100 flex items-center gap-2 hover:bg-cyan-500/30"
                >
                    REVIEW <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
}

const RULE_LABELS = {
    round: "Round Training",
    rapid: "Rapid Training",
    solo_drill: "Solo Drill",
};

// -------------------- Solo Drill flow ----------------------------------------

function SoloDrillFlow({ onBack, onDeploy, mapId, onPickMap }) {
    const [targets, setTargets] = useState("static"); // static | moving
    const [enemyCount, setEnemyCount] = useState(3);
    const [showDamage, setShowDamage] = useState(true);
    const [showHitboxes, setShowHitboxes] = useState(false);
    const [invulnerable, setInvulnerable] = useState(false);
    const [infiniteAmmo, setInfiniteAmmo] = useState(true);

    return (
        <div className="max-w-6xl mx-auto px-8 py-6">
            <FlowSubheader onBack={onBack} crumb="PRACTICE · SOLO DRILL" title="SOLO DRILL" />
            <div className="axis-mono text-sm text-white/60 mb-6 max-w-2xl">
                Enter a map alone. Spawn targets, tweak weapons, test recoil and damage.
                Great for warm-ups before a real match.
            </div>

            <SectionLabel>MAP</SectionLabel>
            <MapPicker teamSize="1v1" value={mapId} onChange={onPickMap} allowUnplayable={false} />

            <SectionLabel>TARGET TYPE</SectionLabel>
            <ChipPicker
                options={[
                    { id: "static", label: "Static Targets", description: "Fixed positions" },
                    { id: "moving", label: "Moving Targets", description: "Simple patrols" },
                ]}
                value={targets}
                onChange={setTargets}
                testIdPrefix="drill-targets-"
            />

            <SectionLabel>TARGET COUNT</SectionLabel>
            <ChipPicker
                options={[
                    { id: 0, label: "None" },
                    { id: 3, label: "3" },
                    { id: 6, label: "6" },
                    { id: 10, label: "10" },
                ]}
                value={enemyCount}
                onChange={setEnemyCount}
                testIdPrefix="drill-count-"
            />

            <SectionLabel>ASSIST OPTIONS</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ToggleRow label="Invulnerable" sub="You take no damage" value={invulnerable} onChange={setInvulnerable} testId="drill-invuln" />
                <ToggleRow label="Infinite Ammo" sub="No reserve depletion" value={infiniteAmmo} onChange={setInfiniteAmmo} testId="drill-infinite-ammo" />
                <ToggleRow label="Show Damage Numbers" sub="Floating hit values" value={showDamage} onChange={setShowDamage} testId="drill-show-dmg" />
                <ToggleRow label="Show Hitboxes" sub="Overlay collision volumes" value={showHitboxes} onChange={setShowHitboxes} testId="drill-show-hitbox" />
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <button
                    onClick={onBack}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-6 py-3 text-xs flex items-center gap-2"
                >
                    <ChevronLeft size={14} /> Back
                </button>
                <button
                    data-testid={PLAY.deploy}
                    onClick={() => onDeploy({ enemyCount, targets, showDamage, showHitboxes, invulnerable, infiniteAmmo })}
                    className="axis-btn axis-cut-sm px-8 py-3 text-sm border border-cyan-400 bg-cyan-500/15 text-cyan-100 flex items-center gap-2 hover:bg-cyan-500/30"
                >
                    <Play size={14} /> DEPLOY DRILL
                </button>
            </div>
        </div>
    );
}

// -------------------- Servers Flow -------------------------------------------

function ServersFlow({ onUnavailable }) {
    const [tab, setTab] = useState("browse");
    return (
        <div className="max-w-6xl mx-auto px-8 py-6">
            <div className="flex items-center gap-1 p-1 border border-white/10 axis-cut-sm bg-white/[0.02] w-fit mb-6">
                <TabBtn testId={PLAY.serversBrowse} active={tab === "browse"} onClick={() => setTab("browse")} label="BROWSE SERVERS" />
                <TabBtn testId={PLAY.serversCreate} active={tab === "create"} onClick={() => setTab("create")} label="CREATE SERVER" />
            </div>

            {tab === "browse" ? (
                <ServersBrowse onUnavailable={onUnavailable} />
            ) : (
                <ServersCreate onUnavailable={onUnavailable} />
            )}
        </div>
    );
}

function TabBtn({ testId, active, onClick, label }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`axis-cut-sm px-4 py-1.5 axis-mono text-[10px] tracking-[0.4em] transition ${
                active
                    ? "bg-orange-400/15 text-orange-200 border border-orange-400/60"
                    : "text-white/50 border border-transparent hover:text-white/80"
            }`}
        >
            {label}
        </button>
    );
}

function ServersBrowse({ onUnavailable }) {
    const [query, setQuery] = useState("");
    return (
        <div className="axis-cut border border-white/10 p-6 bg-black/30">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-2 bg-black/60 border border-white/15 axis-cut-sm px-3 py-1.5 flex-1 min-w-[240px]">
                    <Search size={14} className="text-white/40" />
                    <input
                        data-testid="server-search"
                        placeholder="Server name or ID"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="bg-transparent outline-none text-sm text-white flex-1"
                    />
                </div>
                <button className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-2 text-xs flex items-center gap-2">
                    <RefreshCw size={13} /> REFRESH
                </button>
                {["Region", "Mode", "Team", "Map", "Password", "Empty", "Ping"].map((f) => (
                    <span
                        key={f}
                        className="axis-mono text-[10px] tracking-[0.4em] px-2 py-1 border border-white/10 axis-cut-sm text-white/50"
                    >
                        {f.toUpperCase()}
                    </span>
                ))}
            </div>

            <div
                data-testid="servers-empty-state"
                className="text-center py-16 axis-cut-sm border border-dashed border-white/10 bg-black/40"
            >
                <ShieldOff size={28} className="mx-auto text-white/30 mb-3" />
                <div className="axis-heading text-lg tracking-widest text-white">
                    No public servers are currently available in the Beta version
                </div>
                <div className="axis-mono text-sm text-white/50 mt-2 max-w-md mx-auto">
                    Custom online sessions will appear here once matchmaking and hosting are live.
                </div>
                <button
                    onClick={() =>
                        onUnavailable({
                            crumb: "SERVERS · BROWSE",
                            title: "Server Browser is not yet online",
                            body: "Custom multiplayer servers are planned for a future Beta update. In the meantime you can play Practice matches against bots.",
                        })
                    }
                    className="mt-4 axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs"
                >
                    LEARN MORE
                </button>
            </div>
        </div>
    );
}

function ServersCreate({ onUnavailable }) {
    const [id, setId] = useState(() => genServerId());
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [region, setRegion] = useState("EU-WEST");
    const [maxPlayers, setMaxPlayers] = useState(10);
    const [rule, setRule] = useState("round");
    const [teamSize, setTeamSize] = useState("5v5");
    const [mapId, setMapId] = useState("warehouse");
    const [visibility, setVisibility] = useState("public");

    const attemptCreate = () => {
        onUnavailable({
            crumb: "SERVERS · CREATE",
            title: "Custom Server hosting is planned for a future Beta update",
            body:
                "Server hosting requires online multiplayer, which is still in development. Your configuration is preserved so you can review it once hosting goes live.",
            showPractice: true,
        });
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="axis-cut border border-white/10 p-5 bg-black/30 space-y-3">
                <FormRow label="Server Name">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. NIGHT-OWL LOBBY"
                        className="w-full bg-black/60 border border-white/15 axis-cut-sm px-3 py-2 text-white text-sm outline-none focus:border-orange-400"
                    />
                </FormRow>
                <FormRow label="Server ID">
                    <div className="flex items-center gap-2">
                        <input
                            data-testid={PLAY.serverIdField}
                            value={id}
                            readOnly
                            className="flex-1 bg-black/60 border border-white/15 axis-cut-sm px-3 py-2 text-white axis-heading tracking-[0.4em] outline-none"
                        />
                        <button
                            data-testid={PLAY.serverIdCopy}
                            onClick={() => navigator.clipboard?.writeText(id)}
                            className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-2 text-xs flex items-center gap-2"
                            title="Copy ID"
                        >
                            <Copy size={13} />
                        </button>
                        <button
                            data-testid={PLAY.serverIdRegenerate}
                            onClick={() => setId(genServerId())}
                            className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-2 text-xs flex items-center gap-2"
                            title="Regenerate ID"
                        >
                            <RefreshCw size={13} />
                        </button>
                    </div>
                </FormRow>
                <FormRow label="Password (optional)">
                    <input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type="password"
                        placeholder="Leave empty for public"
                        className="w-full bg-black/60 border border-white/15 axis-cut-sm px-3 py-2 text-white text-sm outline-none focus:border-orange-400"
                    />
                </FormRow>
                <FormRow label="Region">
                    <SelectField
                        value={region}
                        onChange={setRegion}
                        options={["EU-WEST", "EU-EAST", "NA-EAST", "NA-WEST", "APAC", "SA"]}
                    />
                </FormRow>
                <FormRow label="Visibility">
                    <SelectField
                        value={visibility}
                        onChange={setVisibility}
                        options={["public", "private"]}
                    />
                </FormRow>
            </div>

            <div className="axis-cut border border-white/10 p-5 bg-black/30 space-y-3">
                <FormRow label="Game Rule">
                    <SelectField
                        value={rule}
                        onChange={setRule}
                        options={[
                            { value: "round", label: "Round Combat" },
                            { value: "rapid", label: "Rapid Assault" },
                        ]}
                    />
                </FormRow>
                <FormRow label="Team Size">
                    <SelectField
                        value={teamSize}
                        onChange={setTeamSize}
                        options={TEAM_SIZE_CATALOG.map((t) => ({ value: t.id, label: t.label }))}
                    />
                </FormRow>
                <FormRow label="Map">
                    <SelectField
                        value={mapId}
                        onChange={setMapId}
                        options={mapsForTeamSize(teamSize).map((m) => ({ value: m.id, label: m.name }))}
                    />
                </FormRow>
                <FormRow label="Max Players">
                    <input
                        type="number"
                        value={maxPlayers}
                        onChange={(e) => setMaxPlayers(parseInt(e.target.value, 10) || 2)}
                        min={2}
                        max={60}
                        className="w-full bg-black/60 border border-white/15 axis-cut-sm px-3 py-2 text-white text-sm outline-none focus:border-orange-400"
                    />
                </FormRow>
                <div className="flex items-center gap-3 pt-2">
                    <button
                        data-testid={PLAY.serverCreateSubmit}
                        onClick={attemptCreate}
                        className="flex-1 axis-btn axis-cut-sm px-5 py-3 text-xs border border-orange-400 bg-orange-500/15 text-orange-100 hover:bg-orange-500/30 flex items-center justify-center gap-2"
                    >
                        <Server size={13} /> CREATE SERVER
                        <Lock size={11} className="text-orange-300" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function genServerId() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
}

function FormRow({ label, children }) {
    return (
        <div>
            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40 mb-1">{label}</div>
            {children}
        </div>
    );
}
function SelectField({ value, onChange, options }) {
    const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o.toUpperCase() } : o));
    return (
        <select
            value={value}
            onChange={(e) => {
                const v = e.target.value;
                const num = Number(v);
                onChange(Number.isNaN(num) || v === "" ? v : (String(num) === v ? num : v));
            }}
            className="w-full bg-black/60 border border-white/15 axis-cut-sm px-3 py-2 text-white text-sm outline-none focus:border-orange-400"
        >
            {norm.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}

// -------------------- Review screen ------------------------------------------

function ReviewScreen({ cfg, onBack, onDeploy, onSavePreset }) {
    const rows = [
        { label: "Category", value: cfg.category },
        { label: "Mode", value: cfg.mode },
        { label: "Map", value: cfg.mapName?.toUpperCase() },
        { label: "Team Size", value: cfg.teamSize?.toUpperCase() },
        { label: "Weather", value: cfg.weather?.toUpperCase() },
        { label: "Time Of Day", value: cfg.timeOfDay?.toUpperCase() },
        cfg.rule === "round"
            ? { label: "Rounds To Win", value: cfg.rounds }
            : { label: "Time Limit", value: `${Math.round(cfg.timeLimit / 60)} min` },
        cfg.rule === "rapid" && { label: "Score Limit", value: cfg.scoreLimit ? `${cfg.scoreLimit} pts` : "Timer only" },
        { label: "Enemy Bots", value: ENEMY_DIFFICULTY[cfg.enemyDiff]?.label?.toUpperCase() },
        { label: "Friendly Bots", value: FRIENDLY_DIFFICULTY[cfg.friendlyDiff]?.label?.toUpperCase() },
        { label: "Friendly Fire", value: cfg.friendlyFire ? "ON" : "OFF" },
        { label: "Performance", value: cfg.perfHint?.label, color: cfg.perfHint?.color },
    ].filter(Boolean);

    return (
        <div data-testid={PLAY.review} className="max-w-5xl mx-auto px-8 py-6">
            <StepBar current={2} steps={["Configure", "Review", "Deploy"]} />
            <div className="axis-heading text-3xl tracking-[0.3em] text-white mb-2">MISSION REVIEW</div>
            <div className="axis-mono text-sm text-white/60 mb-6">
                Confirm your loadout. You can adjust settings from the previous step or save these as a preset for next time.
            </div>

            <div className="axis-cut border border-white/10 bg-black/40 divide-y divide-white/5">
                {rows.map((r) => (
                    <div key={r.label} className="flex items-center justify-between px-5 py-3">
                        <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50">
                            {r.label.toUpperCase()}
                        </div>
                        <div
                            className="axis-heading text-sm tracking-widest"
                            style={{ color: r.color || "#fff" }}
                        >
                            {r.value}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
                <button
                    onClick={onBack}
                    className="axis-btn axis-btn-ghost axis-cut-sm px-6 py-3 text-xs flex items-center gap-2"
                >
                    <ChevronLeft size={14} /> BACK
                </button>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onSavePreset}
                        data-testid={PLAY.savePreset}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-5 py-3 text-xs flex items-center gap-2"
                    >
                        <Bookmark size={13} /> SAVE PRESET
                    </button>
                    <button
                        data-testid={PLAY.deploy}
                        onClick={onDeploy}
                        className="axis-btn axis-cut-sm px-8 py-3 text-sm border border-cyan-400 bg-cyan-500/15 text-cyan-100 hover:bg-cyan-500/30 flex items-center gap-2"
                    >
                        <Play size={14} className="fill-current" /> DEPLOY
                    </button>
                </div>
            </div>
        </div>
    );
}

// -------------------- Shared pickers -----------------------------------------

function SectionLabel({ children }) {
    return (
        <div className="flex items-center gap-3 mt-6 mb-3">
            <div className="h-px w-8 bg-orange-400/60" />
            <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/90">{children}</div>
            <div className="h-px flex-1 bg-white/10" />
        </div>
    );
}

function FlowSubheader({ onBack, crumb, title }) {
    return (
        <div className="flex items-center justify-between mb-6">
            <button
                onClick={onBack}
                className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
            >
                <ChevronLeft size={14} /> Back
            </button>
            <div className="text-center">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">{crumb}</div>
                <div className="axis-heading text-2xl tracking-[0.4em] text-white mt-0.5">{title}</div>
            </div>
            <div className="w-[80px]" />
        </div>
    );
}

function StepBar({ current, steps }) {
    return (
        <div className="flex items-center gap-3 mb-6">
            {steps.map((label, i) => {
                const active = i === current - 1;
                const done = i < current - 1;
                return (
                    <div key={label} className="flex items-center gap-3">
                        <div
                            className={`w-6 h-6 flex items-center justify-center axis-cut-sm axis-mono text-[10px] tracking-[0.3em] border ${
                                active
                                    ? "border-cyan-400 text-cyan-200 bg-cyan-500/10"
                                    : done
                                      ? "border-white/30 text-white/70"
                                      : "border-white/10 text-white/30"
                            }`}
                        >
                            {i + 1}
                        </div>
                        <div className={`axis-mono text-[10px] tracking-[0.4em] ${active ? "text-white" : "text-white/40"}`}>
                            {label.toUpperCase()}
                        </div>
                        {i < steps.length - 1 && <div className="w-8 h-px bg-white/10" />}
                    </div>
                );
            })}
        </div>
    );
}

function RulePicker({ value, onChange, includeSoloDrill }) {
    const rules = [
        {
            id: "round",
            testId: PLAY.ruleRound,
            title: "ROUND COMBAT",
            icon: Target,
            accent: "#00e5ff",
            description:
                "Blue vs Red. Eliminate the enemy team to win a round; first side to the required round count wins the match. Dead players spectate teammates until the next round.",
        },
        {
            id: "rapid",
            testId: PLAY.ruleRapid,
            title: "RAPID ASSAULT",
            icon: Zap,
            accent: "#ff9a4a",
            description:
                "Continuous respawns. Eliminations earn points; headshots pay bonus. First to the score limit — or highest score when the timer expires — wins.",
        },
    ];
    if (includeSoloDrill) {
        rules.push({
            id: "solo_drill",
            testId: PLAY.ruleSoloDrill,
            title: "SOLO DRILL",
            icon: Sparkles,
            accent: "#c084fc",
            description:
                "Enter a map alone with configurable dummy targets. Warm-up your aim, test loadouts and hitboxes without match pressure.",
        });
    }

    return (
        <div className={`grid grid-cols-1 md:grid-cols-${rules.length === 3 ? 3 : 2} gap-3`}>
            {rules.map((r) => {
                const Icon = r.icon;
                const active = value === r.id;
                return (
                    <button
                        key={r.id}
                        data-testid={r.testId}
                        onClick={() => onChange(r.id)}
                        className={`text-left axis-cut border p-4 transition ${
                            active
                                ? "border-white bg-white/[0.05]"
                                : "border-white/10 bg-white/[0.02] hover:border-white/25"
                        }`}
                        style={{ boxShadow: active ? `0 0 24px ${r.accent}22` : "none" }}
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Icon size={16} style={{ color: r.accent }} />
                            <span className="axis-heading text-lg tracking-[0.2em]" style={{ color: r.accent }}>
                                {r.title}
                            </span>
                        </div>
                        <div className="axis-mono text-xs text-white/60 leading-relaxed">{r.description}</div>
                    </button>
                );
            })}
        </div>
    );
}

function TeamSizePicker({ value, onChange, sizes }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {sizes.map((t) => {
                const active = value === t.id;
                const experimental = t.perfCost === "extreme";
                return (
                    <button
                        key={t.id}
                        data-testid={`${PLAY.teamPrefix}${t.id}`}
                        onClick={() => onChange(t.id)}
                        className={`axis-cut-sm py-3 px-3 border text-center transition ${
                            active
                                ? "border-cyan-400 bg-cyan-400/10"
                                : "border-white/10 bg-white/[0.02] hover:border-cyan-400/60"
                        }`}
                    >
                        <div className="axis-heading text-lg text-white">{t.label}</div>
                        <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40">{t.desc.toUpperCase()}</div>
                        {experimental && (
                            <div className="axis-mono text-[9px] tracking-[0.3em] text-orange-300/80 mt-1">
                                EXPERIMENTAL
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function MapPicker({ teamSize, value, onChange, allowUnplayable }) {
    const options = mapsForTeamSize(teamSize);
    if (options.length === 0) {
        return (
            <div className="axis-cut-sm border border-white/10 p-6 text-white/50 axis-mono text-sm">
                No maps configured for this team size yet.
            </div>
        );
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {options.map((m) => {
                const Icon = m.icon;
                const active = value === m.id;
                const locked = !m.playable && !allowUnplayable;
                const pv = m.preview || {};
                return (
                    <button
                        key={m.id}
                        data-testid={`${PLAY.mapPrefix}${m.id}`}
                        onClick={() => !locked && onChange(m.id)}
                        disabled={locked}
                        className={`relative text-left axis-cut-sm p-3 transition-all border ${
                            active
                                ? "border-cyan-400 bg-cyan-400/10"
                                : locked
                                  ? "border-white/10 bg-white/[0.02] opacity-55 cursor-not-allowed"
                                  : "border-white/10 bg-white/[0.02] hover:border-cyan-400/60"
                        }`}
                    >
                        {/* Preview panel — layered gradient using the map palette */}
                        <div
                            className="relative w-full h-32 axis-cut-sm mb-3 overflow-hidden"
                            style={{
                                background: pv.palette
                                    ? `linear-gradient(135deg, ${pv.palette[0]}dd 0%, ${pv.palette[1]}88 50%, ${pv.palette[2]}66 100%)`
                                    : `linear-gradient(135deg, ${m.tint}77 0%, #050505 100%)`,
                            }}
                        >
                            {/* Ambient noise dust */}
                            <div className="absolute inset-0 opacity-30 mix-blend-overlay"
                                 style={{ background: "radial-gradient(400px 120px at 20% 20%, rgba(255,255,255,0.15), transparent 60%)" }} />
                            {/* Distant silhouette shapes (schematic) */}
                            <SchematicOverlay id={m.id} />
                            <Icon size={30} className="absolute right-3 top-3 text-white/90" strokeWidth={1.4} />
                            {/* Status pill */}
                            <div
                                className="absolute left-2 top-2 axis-mono text-[9px] tracking-[0.3em] px-1.5 py-0.5 border axis-cut-sm"
                                style={{
                                    borderColor: m.playable ? "#00ff8855" : "#ffb30055",
                                    color: m.playable ? "#00ff88" : "#ffb300",
                                    background: "rgba(0,0,0,0.35)",
                                }}
                            >
                                {m.status}
                            </div>
                            {locked && (
                                <div className="absolute right-3 bottom-3 px-1.5 py-0.5 border border-amber-400/60 axis-cut-sm bg-black/40">
                                    <Lock size={11} className="text-amber-300" />
                                </div>
                            )}
                        </div>

                        <div className="flex items-baseline justify-between">
                            <div>
                                <div className="axis-heading text-lg text-white tracking-widest">
                                    {m.name}
                                </div>
                                <div className="axis-mono text-[9px] tracking-[0.3em] text-white/50 mt-0.5">
                                    {pv.environment || m.subtitle}
                                </div>
                            </div>
                            {pv.difficulty && (
                                <div
                                    className="axis-mono text-[9px] tracking-[0.3em] px-1.5 py-0.5 border axis-cut-sm text-white/70"
                                    style={{ borderColor: "rgba(255,154,74,0.4)" }}
                                >
                                    {pv.difficulty}
                                </div>
                            )}
                        </div>
                        {pv.tagline && (
                            <div className="axis-mono text-xs text-white/60 mt-2 leading-relaxed">
                                {pv.tagline}
                            </div>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2 axis-mono text-[9px] tracking-[0.3em] text-white/40">
                            {pv.recommendedPlayers && (
                                <span>PLAYERS · {pv.recommendedPlayers}</span>
                            )}
                            <span>SIZE · {teamSize.toUpperCase()}</span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// Cheap SVG silhouette per map so cards feel distinct
function SchematicOverlay({ id }) {
    // Each map returns a set of SVG shapes that read as a schematic
    const style = { position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.35 };
    switch (id) {
        case "training_yard":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <rect x="10" y="10" width="180" height="80" fill="none" stroke="#fff" strokeWidth="1" strokeDasharray="4 3" />
                    <rect x="45" y="30" width="20" height="18" fill="#fff2" />
                    <rect x="130" y="45" width="24" height="12" fill="#fff2" />
                    <rect x="85" y="60" width="30" height="10" fill="#fff2" />
                </svg>
            );
        case "factory":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <rect x="10" y="20" width="180" height="60" fill="none" stroke="#fff" strokeWidth="1" />
                    <rect x="95" y="30" width="10" height="45" fill="#fff3" />
                    {[30, 60, 130, 155].map((x) => <rect key={x} x={x} y="45" width="16" height="20" fill="#fff2" />)}
                </svg>
            );
        case "warehouse":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <rect x="10" y="15" width="180" height="70" fill="none" stroke="#fff" strokeWidth="1" />
                    {[25, 55, 85, 115, 145, 175].map((x) => (
                        <rect key={x} x={x} y="25" width="8" height="50" fill="#fff3" />
                    ))}
                </svg>
            );
        case "desert_base":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <rect x="10" y="15" width="180" height="70" fill="none" stroke="#fff" strokeWidth="1" />
                    <circle cx="100" cy="50" r="18" fill="#fff2" />
                    {[[35, 30], [165, 30], [35, 70], [165, 70]].map(([x, y], i) => (
                        <rect key={i} x={x - 6} y={y - 6} width="12" height="12" fill="#fff3" />
                    ))}
                </svg>
            );
        case "highlands":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <path d="M 10 80 L 60 30 L 100 60 L 140 20 L 190 80 Z" fill="#fff2" stroke="#fff" strokeWidth="1" />
                </svg>
            );
        case "downtown":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    {[15, 45, 75, 110, 145, 175].map((x, i) => (
                        <rect key={x} x={x} y={20 + (i % 3) * 8} width="18" height={60 - (i % 3) * 8} fill="#fff3" />
                    ))}
                </svg>
            );
        case "citadel":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <rect x="10" y="30" width="180" height="55" fill="none" stroke="#fff" strokeWidth="1" />
                    <rect x="80" y="10" width="40" height="30" fill="#fff3" />
                </svg>
            );
        case "wildfire_ridge":
            return (
                <svg viewBox="0 0 200 100" style={style}>
                    <path d="M 10 80 Q 60 40 100 60 T 190 70" fill="none" stroke="#fff" strokeWidth="1" />
                </svg>
            );
        default:
            return null;
    }
}

function ChipPicker({ options, value, onChange, testIdPrefix }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => {
                const active = value === o.id;
                return (
                    <button
                        key={o.id}
                        data-testid={`${testIdPrefix}${o.id}`}
                        onClick={() => onChange(o.id)}
                        className={`axis-cut-sm px-4 py-2 border text-left min-w-[110px] transition ${
                            active
                                ? "border-cyan-400 bg-cyan-400/10"
                                : "border-white/10 bg-white/[0.02] hover:border-cyan-400/60"
                        }`}
                    >
                        <div className="axis-heading text-sm text-white">{o.label}</div>
                        {o.description && (
                            <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40 mt-0.5">
                                {o.description.toUpperCase()}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

function DifficultyPicker({ value, onChange, table, testIdPrefix, accent = "#ff9a4a" }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {DIFFICULTY_KEYS.map((id) => {
                const d = table[id];
                if (!d) return null;
                const active = value === id;
                return (
                    <button
                        key={id}
                        data-testid={`${testIdPrefix}${id}`}
                        onClick={() => onChange(id)}
                        className={`axis-cut-sm py-3 px-3 border text-left transition ${
                            active ? "bg-white/[0.05]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                        }`}
                        style={{
                            borderColor: active ? accent : undefined,
                            boxShadow: active ? `0 0 18px ${accent}44` : "none",
                        }}
                    >
                        <div className="flex items-center gap-2">
                            <Bot size={12} style={{ color: accent }} />
                            <span className="axis-heading text-sm" style={{ color: accent }}>
                                {d.label}
                            </span>
                        </div>
                        <div className="axis-mono text-[9px] tracking-[0.2em] text-white/50 mt-1 leading-relaxed">
                            {d.description}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function ToggleRow({ label, sub, value, onChange, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={() => onChange(!value)}
            className={`text-left axis-cut-sm border p-3 transition flex items-center justify-between ${
                value ? "border-cyan-400 bg-cyan-500/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
            }`}
        >
            <div>
                <div className="axis-heading text-sm text-white">{label}</div>
                {sub && (
                    <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40 mt-0.5">
                        {sub.toUpperCase()}
                    </div>
                )}
            </div>
            <div
                className={`w-9 h-5 rounded-full relative transition ${
                    value ? "bg-cyan-400/70" : "bg-white/10"
                }`}
            >
                <div
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                        value ? "left-4" : "left-0.5"
                    }`}
                />
            </div>
        </button>
    );
}
